import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import MainLayout from "layouts/Main";
import { NORD_POOL_TABS } from "helpers/tabs";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchAllProductionPark } from "store/appSlice";
import { selectProductionParks } from "store/selectors";
import ParkSelect from "views/Dashboard/Forecasts/Production/ParkSelect";
import { getIdcTransactions } from "api/resources/idcTransactions";
import { IdcTransaction } from "api/models/IdcTransaction";

const formatMtu = (transaction: IdcTransaction) =>
  `${dayjs(transaction.mtuStart).format("DD.MM.YYYY HH:mm")} - ${dayjs(transaction.mtuEnd).format("HH:mm")}`;

const NordPoolIdcTransactions = () => {
  const dispatch = useAppDispatch();
  const productionParks = useAppSelector(selectProductionParks);

  const [selectedParks, setSelectedParks] = useState<
    { title: string; value: number }[]
  >([]);
  const [from, setFrom] = useState<Dayjs>(dayjs().startOf("day"));
  const [to, setTo] = useState<Dayjs>(dayjs().startOf("day").add(2, "day"));
  const [transactions, setTransactions] = useState<IdcTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    type: "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    dispatch(fetchAllProductionPark(0, 1000, {}));
  }, [dispatch]);

  const parks = useMemo(
    () =>
      productionParks?.content?.map((park) => ({
        title: park.name,
        value: park.id,
      })) || [],
    [productionParks],
  );

  const parkIds = useMemo(
    () => selectedParks.map((park) => park.value),
    [selectedParks],
  );

  useEffect(() => {
    if (!from?.isValid() || !to?.isValid() || to.isBefore(from)) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    getIdcTransactions(from.utc().format(), to.utc().format(), parkIds)
      .then(({ data }) => {
        if (!cancelled) {
          setTransactions(data);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch IDC transactions", error);
        if (!cancelled) {
          setSnackbar({
            type: "error",
            message: "Failed to fetch IDC transactions",
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [from, to, parkIds]);

  return (
    <MainLayout
      tabs={NORD_POOL_TABS}
      snackbar={{
        open: !!snackbar,
        handleClose: () => setSnackbar(null),
        type: snackbar?.type,
        message: snackbar?.message || "",
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">IDC/Elbas</Typography>
      </Box>
      <Box sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
        <ParkSelect
          parks={parks}
          selectedParks={selectedParks}
          setSelectedParks={setSelectedParks}
          label="Parks"
          placeholder="All parks"
          minWidth={360}
          maxWidth={360}
        />
        <FormControl sx={{ minWidth: 240, background: "white" }} size="small">
          <DateTimePicker
            label="MTU start from"
            ampm={false}
            minutesStep={15}
            format="DD.MM.YYYY HH:mm"
            slotProps={{ textField: { size: "small" } }}
            value={from}
            onChange={(value) => value && setFrom(value)}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 240, background: "white" }} size="small">
          <DateTimePicker
            label="MTU start to"
            ampm={false}
            minutesStep={15}
            format="DD.MM.YYYY HH:mm"
            slotProps={{ textField: { size: "small" } }}
            value={to}
            minDateTime={from}
            onChange={(value) => value && setTo(value)}
          />
        </FormControl>
      </Box>
      <TableContainer sx={{ maxWidth: 900 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Park name</TableCell>
              <TableCell>MTU</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Value (kW)</TableCell>
              <TableCell>Adjusts supply plan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{transaction.parkName}</TableCell>
                <TableCell>{formatMtu(transaction)}</TableCell>
                <TableCell align="right">
                  {Number(transaction.price).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  {Number(transaction.value).toFixed(3)}
                </TableCell>
                <TableCell>
                  {transaction.adjustNordpoolSupplyPlan ? "Yes" : "No"}
                </TableCell>
              </TableRow>
            ))}
            {!loading && transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  No IDC transactions for the selected filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </MainLayout>
  );
};

export default NordPoolIdcTransactions;
